import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function BusinessModelUnitEconomics() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Economics">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Expert ASO at the price of a tool.
        </h1>
        <p className="mt-3 text-base leading-snug text-slate-700 md:mt-4 md:text-xl">
          Apptonomy moves{" "}
          <span className="font-semibold text-slate-900">
            organic installs
          </span>{" "}
          — visibility × conversion — on every app, in every market. Both
          inputs decay without sustained ASO; both are addressable on the
          customer's own store data. The business consequence is{" "}
          <span className="font-semibold text-slate-900">
            lower blended CAC
          </span>{" "}
          — growing the cheapest channel a publisher owns.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
        <Card label="Pricing tiers (live)">
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Tier</th>
                <th className="pb-1.5 text-right">Price/mo</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <PricingRow tier="Free" price="€0" />
              <PricingRow tier="Basic" price="€49" />
              <PricingRow tier="Pro" price="€149" />
              <PricingRow tier="Elite" price="€399" />
              <PricingRow tier="Enterprise" price="Flexible" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Annual billing: 17% off. Self-serve signup; free trial; Stripe
            billing; 30% affiliate revenue share.
          </p>
        </Card>

        <Card label="m12 mix → ARR">
          <p className="mt-3 text-2xl font-bold tracking-tight text-blue-700 md:text-3xl">
            €1.55M ARR
          </p>
          <p className="text-xs text-slate-500 md:text-sm">
            700 paying accounts · €184 blended ARPU
          </p>
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Tier mix</th>
                <th className="pb-1.5 text-right">Accts</th>
                <th className="pb-1.5 text-right">Mo. rev</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <MixRow tier="Basic 45%" accts="315" rev="€15.4K" />
              <MixRow tier="Pro 40%" accts="280" rev="€41.7K" />
              <MixRow tier="Elite 10%" accts="70" rev="€27.9K" />
              <MixRow tier="Enterprise 5%" accts="35" rev="€43.8K" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Enterprise ≈ 34% of ARR on 5% of accounts.
          </p>
        </Card>

        <Card label="Unit economics (steady-state)">
          <dl className="mt-3 space-y-2 text-xs md:text-sm">
            <UEMetric label="Gross margin" value="≥ 85%" sub="~99% today" />
            <UEMetric label="LTV" value="€4,416" sub="€184 × 24 mo" />
            <UEMetric
              label="Blended CAC"
              value="~€175"
              sub="≤ €220 ceiling"
            />
            <UEMetric label="LTV:CAC" value="≥ 5:1" sub="model ~25:1" />
            <UEMetric label="Payback" value="≤ 8 mo" sub="model ~1.1 mo" />
          </dl>
        </Card>
      </div>

      <div className="mt-5 rounded-2xl bg-blue-50 px-5 py-4 ring-1 ring-blue-200 md:mt-6 md:px-8 md:py-5">
        <p className="text-base font-medium italic leading-snug text-blue-900 md:text-xl">
          Grow your cheapest channel. Lower your blended CAC. Proven on your
          own numbers.
        </p>
      </div>

      <div className="mt-auto pt-5">
        <p className="text-xs leading-relaxed text-slate-500">
          <span className="font-semibold text-slate-700">
            Attribution discipline.
          </span>{" "}
          Conversion-rate and visibility deltas on the customer's own App
          Store Connect / Play Console data, tied via Score History to
          specific published changes. Causal install attribution is the
          question a paid pilot or geo-holdout answers — never claimed from
          the dashboard.
        </p>
      </div>
    </div>
  );
}

function Card({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
        {label}
      </p>
      {children}
    </div>
  );
}

function PricingRow({ tier, price }: { tier: string; price: string }) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{price}</td>
    </tr>
  );
}

function MixRow({
  tier,
  accts,
  rev,
}: {
  tier: string;
  accts: string;
  rev: string;
}) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{accts}</td>
      <td className="py-1.5 text-right tabular-nums">{rev}</td>
    </tr>
  );
}

function UEMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2 last:border-b-0">
      <div>
        <dt className="font-medium text-slate-700">{label}</dt>
        <dd className="text-[10px] text-slate-500 md:text-xs">{sub}</dd>
      </div>
      <span className="tabular-nums font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}
